interface InpAttribution {
  testId?: string;
  componentId?: string;
  elementId?: string;
  tagName?: string;
  className?: string;
  selector?: string;
  text?: string;
}

interface InpPayload {
  timestamp: string;
  eventName: 'cwv_inp';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  /** 'pointer' | 'keyboard' — matches web-vitals' interactionType. */
  interactionType: string;
  /** The DOM event that dominated the interaction, e.g. 'click', 'keydown'. */
  eventType: string;
  /** Total interactions on the page; shows how much the percentile forgave. */
  interactionCount: number;
  inputDelay: number;
  processingTime: number;
  presentationDelay: number;
  url: string;
  pageName?: string;
  userAgent: string;
  deviceType: string;
  isMobile: boolean;
  appId?: string;
  attribution: InpAttribution;
}

const API_ENDPOINT = `${process.env.NEXT_PUBLIC_UBA_AUDIT_URL}/api/cwv-inp-audit`;
const MAX_SELECTOR_DEPTH = 5;
const MAX_TEXT_LEN = 40;

// Chrome only exposes event entries at 8ms granularity and web-vitals ignores
// anything faster, so 40ms keeps the observer cheap without losing signal.
const MIN_DURATION = 40;

/**
 * The page's worst interaction (a tap is pointerdown + pointerup + click, all
 * sharing an interactionId — they fold into one of these, not three).
 *
 * Note this is the page MAX, not INP. Real INP forgives one interaction per 50,
 * so on a page with many interactions this reads slightly pessimistic vs CrUX.
 * That is deliberate: the goal here is finding slow interactions to fix, not
 * reproducing the CrUX number.
 */
interface Interaction {
  id: number;
  /** Longest single-entry duration; this is the interaction's latency. */
  duration: number;
  /** Event name of the entry that produced `duration`, e.g. 'click'. */
  name: string;
  /** 'pointer' | 'keyboard' — what web-vitals reports as interactionType. */
  kind: string;
  startTime: number;
  processingStart: number;
  processingEnd: number;
  attribution: InpAttribution;
}

const KEYBOARD_EVENTS = new Set(['keydown', 'keyup', 'keypress']);

/**
 * PerformanceEventTiming.interactionId ships in Chrome 96+ but is absent from
 * TypeScript's DOM lib as of 5.8. Non-interaction events (pointerover, mouseout)
 * report 0; browsers without the field report undefined.
 */
type EventTimingWithInteractionId = PerformanceEventTiming & {
  interactionId?: number;
};

class CwvInpLogger {
  private observer: PerformanceObserver | null = null;
  /** Worst interaction on the current page. */
  private worst: Interaction | null = null;
  /** Distinct interactions seen — reported for context, not used to pick. */
  private interactionCount = 0;
  private seenIds = new Set<number>();
  private initialized = false;
  private pageName?: string;

  /**
   * Start observing INP. Safe to call multiple times — only first call wires up.
   * Defers all work to requestIdleCallback so page-load critical path is untouched.
   */
  start(pageName?: string): void {
    if (this.initialized || typeof window === 'undefined') return;
    if (typeof PerformanceObserver === 'undefined') return;

    this.pageName = pageName;

    const schedule =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback
        : (cb: IdleRequestCallback) =>
            setTimeout(() => cb({didTimeout: false, timeRemaining: () => 50}), 1);

    schedule(() => this.init());
  }

  private init(): void {
    if (this.initialized) return;

    const criteria = window?.__FEATURE_FLAGS__?.cwvLoggingCriteria;
    
    if (!criteria?.enableInpLogging) return;

    if (criteria.mobileOnly) {
      const isMobile = /(iPad|iPhone|Android|Mobile)/i.test(navigator.userAgent);
      if (!isMobile) return;
    }

    if (criteria.pageNames?.length && this.pageName) {
      // "/" means the homepage only — as a prefix it would match every path.
      const matches = criteria.pageNames.some(prefix =>
        prefix === '/'
          ? this.pageName === '/'
          : this.pageName!.startsWith(prefix)
      );
      if (!matches) return;
    }

    const supported =
      PerformanceObserver.supportedEntryTypes?.includes('event') ?? false;
    if (!supported) return;

    this.initialized = true;

    try {
      this.observer = new PerformanceObserver(list => {
        for (const entry of list.getEntries() as EventTimingWithInteractionId[]) {
          this.record(entry);
        }
      });
      this.observer.observe({
        type: 'event',
        buffered: true,
        durationThreshold: MIN_DURATION,
      } as PerformanceObserverInit);
    } catch {
      return;
    }

    const onHidden = () => {
      if (document.visibilityState === 'hidden') this.report();
    };
    document.addEventListener('visibilitychange', onHidden, {capture: true});
    window.addEventListener('pagehide', () => this.report(), {capture: true});
  }

  /**
   * Fold one event entry into the page's worst interaction.
   *
   * Entries with interactionId 0 (pointerover, mouseout, pointercancel and
   * friends) are NOT interactions — Chrome excludes them from INP, so keeping
   * them here would report values that never appear in CrUX.
   */
  private record(entry: EventTimingWithInteractionId): void {
    if (entry.duration < MIN_DURATION) return;

    const id = entry.interactionId;
    // 0 means "not an interaction" and is always dropped. undefined means the
    // browser predates the field; fall back to treating each entry as its own
    // interaction so those users still report something rather than nothing.
    if (id === 0) return;
    const key = id ?? -entry.startTime;

    if (!this.seenIds.has(key)) {
      this.seenIds.add(key);
      this.interactionCount++;
    }

    // A later entry of the interaction we are already tracking (e.g. click
    // after pointerup). Widen the window even when this entry is shorter —
    // the interaction is not over until its last entry paints.
    if (this.worst && this.worst.id === key) {
      this.worst.processingEnd = Math.max(
        this.worst.processingEnd,
        entry.processingEnd
      );
      if (entry.duration > this.worst.duration) {
        this.worst.duration = entry.duration;
        this.worst.name = entry.name;
        this.worst.startTime = entry.startTime;
        this.worst.processingStart = entry.processingStart;
        // Re-attribute to the entry that actually dominated the interaction.
        this.worst.attribution = this.attribute(entry.target as Element | null);
      }
      return;
    }

    if (this.worst && this.worst.duration >= entry.duration) return;

    this.worst = {
      id: key,
      duration: entry.duration,
      name: entry.name,
      kind: KEYBOARD_EVENTS.has(entry.name) ? 'keyboard' : 'pointer',
      startTime: entry.startTime,
      processingStart: entry.processingStart,
      processingEnd: entry.processingEnd,
      // Resolve attribution NOW while the target element is still in the DOM.
      // entry.target becomes null once the element is unmounted (very common in React).
      attribution: this.attribute(entry.target as Element | null),
    };
  }

  /**
   * Send the current page's worst interaction and reset.
   * Pass nextPageName when reporting due to an SPA route change.
   * No-op if there's nothing to report — making it safe to call multiple times.
   */
  report(nextPageName?: string): void {
    if (this.worst) {
      try {
        const payload = this.buildPayload(this.worst);
        // Send as plain string so sendBeacon uses text/plain (CORS-safelisted, no preflight).
        // application/json would trigger a preflight that sendBeacon handles unreliably across browsers.
        const body = JSON.stringify({events: [payload]});
        const sent = navigator.sendBeacon(API_ENDPOINT, body);
        if (!sent && typeof fetch === 'function') {
          fetch(API_ENDPOINT, {
            method: 'POST',
            body,
            keepalive: true,
            headers: {'Content-Type': 'text/plain'},
          }).catch(() => {});
        }
      } catch {
        // best effort — data loss acceptable on unload
      }
    }
    // Reset unconditionally: a page with only non-interaction events still has
    // to start clean, or its counts leak into the next SPA route.
    this.worst = null;
    this.seenIds.clear();
    this.interactionCount = 0;
    if (nextPageName !== undefined) this.pageName = nextPageName;
  }

  private buildPayload(entry: Interaction): InpPayload {
    const userAgent = navigator.userAgent;
    const isMobile = /(iPad|iPhone|Android|Mobile)/i.test(userAgent);
    const deviceType = this.getDeviceOs(userAgent, isMobile);

    // The interaction may span more entries than the one that set `duration`,
    // so its end is whichever is later: the dominant entry's next paint, or the
    // last entry's processing end.
    const end = Math.max(
      entry.startTime + entry.duration,
      entry.processingEnd
    );
    const value = Math.max(entry.duration, end - entry.startTime);

    const inputDelay = Math.max(0, entry.processingStart - entry.startTime);
    const processingTime = Math.max(
      0,
      entry.processingEnd - entry.processingStart
    );
    const presentationDelay = Math.max(0, end - entry.processingEnd);

    return {
      timestamp: new Date().toISOString(),
      eventName: 'cwv_inp',
      value,
      rating: this.rate(value),
      interactionType: entry.kind,
      eventType: entry.name,
      interactionCount: this.interactionCount,
      inputDelay,
      processingTime,
      presentationDelay,
      url: window.location.href,
      pageName: this.pageName,
      userAgent,
      deviceType,
      isMobile,
      attribution: entry.attribution,
    };
  }

  private rate(v: number): 'good' | 'needs-improvement' | 'poor' {
    if (v <= 200) return 'good';
    if (v <= 500) return 'needs-improvement';
    return 'poor';
  }

  private attribute(target: Element | null): InpAttribution {
    if (!target) return {};

    const attribution: InpAttribution = {
      tagName: target.tagName?.toLowerCase(),
      className:
        typeof target.className === 'string'
          ? target.className.slice(0, 120)
          : undefined,
      selector: this.selector(target),
    };

    if (target.id) attribution.elementId = target.id;

    // textContent, not innerText — innerText forces a synchronous layout, and
    // this runs inside the observer callback on a main thread that is already
    // busy enough to have produced a slow interaction.
    const text = target.textContent?.trim().replace(/\s+/g, ' ');
    if (text) attribution.text = text.slice(0, MAX_TEXT_LEN);

    let node: Element | null = target;
    let depth = 0;
    while (node && depth < MAX_SELECTOR_DEPTH) {
      const testId = node.getAttribute?.('data-testid');
      if (testId && !attribution.testId) attribution.testId = testId;
      const cmp = node.getAttribute?.('data-component');
      if (cmp && !attribution.componentId) attribution.componentId = cmp;
      if (attribution.testId && attribution.componentId) break;
      node = node.parentElement;
      depth++;
    }

    return attribution;
  }

  private selector(el: Element): string {
    const parts: string[] = [];
    let node: Element | null = el;
    let depth = 0;
    while (node && depth < MAX_SELECTOR_DEPTH) {
      let part = node.tagName.toLowerCase();
      if (node.id) {
        part += `#${node.id}`;
        parts.unshift(part);
        break;
      }
      if (typeof node.className === 'string' && node.className) {
        const cls = node.className.trim().split(/\s+/).slice(0, 2).join('.');
        if (cls) part += `.${cls}`;
      }
      parts.unshift(part);
      node = node.parentElement;
      depth++;
    }
    return parts.join('>');
  }

  private getDeviceOs(userAgent: string, isMobile: boolean): string {
    if (isMobile) {
      if (/android/i.test(userAgent)) return 'android';
      if (/iphone|ipad|ipod|ios/i.test(userAgent)) return 'ios';
    }
    return 'web';
  }
}

const cwvInpLogger = new CwvInpLogger();

export default cwvInpLogger;
